import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, theme, Popconfirm, Steps, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { RootState } from 'store/configureStore';
import { GapTabs } from 'store/ducks/Gaps/types';
import { clientReqOp } from 'store/ducks/ClientReq';
import { gapsOp } from 'store/ducks/Gaps';
import { solutionsOp } from 'store/ducks/Solutions';
import { tasksOp } from 'store/ducks/Tasks';
import { evidencesOp } from 'store/ducks/Evidences';
import { OptionType } from 'store/ducks/Global/types';
import {
  IAurReq,
  IClientReq,
  IClientReqToBeAdded,
  IRegulatoryReq
} from 'store/ducks/ClientReq/types';
import { TitleKeyComplianceScreens } from 'store/ducks/Management/types';
import { stepsCompliance } from 'constants/steps';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import gapsRequests from 'constants/gapsRequests';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import { IComplianceEstLayoutProps } from 'store/ducks/ComplienceEst/types';
import * as Styled from 'components/Layouts/Layout.styled';

const { Sider } = Layout;
const { useToken } = theme;
const { Content } = Layout;

const ComplianceEstLayout: React.FC<IComplianceEstLayoutProps> = ({
  activeTab,
  activeKey,
  children
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { token } = useToken();
  const steps = useMemo(() => stepsCompliance(), [i18n.language]);
  const current = steps.findIndex((step) => location.pathname === step.url);
  const items = steps.map((step) => ({
    key: step.title,
    title: step.title
  }));

  const {
    lang,
    saveDisabled,
    drawTable,
    appliedFiltersResult,
    checkedElementsToBeMerged,
    checkedElementsToBeCopied,
    mergedElements,
    copiedElements,
    gapsUpdated,
    infoUpdated,
    orgContext,
    scheme,
    clientReqs,
    tasksUpdated
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    saveDisabled: state.complianceEst.saveDisabled,
    appliedFiltersResult: state.clientReq.appliedFiltersResult,
    checkedElementsToBeMerged: state.clientReq.checkedElementsToBeMerged,
    checkedElementsToBeCopied: state.clientReq.checkedElementsToBeCopied,
    mergedElements: state.clientReq.mergedElements,
    copiedElements: state.clientReq.copiedElements,
    gapsUpdated: state.gaps.gapsUpdated,
    infoUpdated: state.gaps.infoUpdated,
    orgContext: state.global.hierarchicalLevelsScope,
    scheme: state.global.scheme,
    clientReqs: state.clientReq.clientReqs,
    tasksUpdated: state.complianceEst.tasksUpdated
  }));

  const industry = orgContext?.industryType || [];
  const page = location.pathname.split('/').pop();

  const [firstOpen, setFirstOpen] = useState(false);
  const [secondOpen, setSecondOpen] = useState(false);
  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const selectedGroupValues = orgContext?.group ?? [];

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await ApiService.getGroupBPR();

        const data = response.data;
        const groups = Array.isArray(data)
          ? data
          : data.groups ?? data.data ?? [];

        setGroupOptions(
          groups.map((item: any) => ({
            label: item.name ?? item.group_name ?? item.label,
            value: item.id ?? item.group_id ?? item.value
          }))
        );
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      }
    };

    fetchGroups();
  }, []);

  // Helper function to get copied items from Redux state
  const getCopiedItemsFromState = (): number[] => {
    return copiedElements || [];
  };

  const handleStepChange = async (stepIndex: number) => {
    if (window.hasUnsavedChanges && window.showUnsavedChangesModal) {
      const shouldProceed = await window.showUnsavedChangesModal();
      if (shouldProceed) {
        navigate(steps[stepIndex].url);
      }
    } else {
      navigate(steps[stepIndex].url);
    }
  };

  const copyAll = () => {
    let selectedElementsToCopy: IClientReqToBeAdded[] = [];
    let idsList: number[] = [];

    Object.values(appliedFiltersResult).map((regReq: IRegulatoryReq) =>
      Object.values(regReq.AUR_REQ).map((aurReq: IAurReq) => {
        const { id, aur_parent_key, aur_child_key, value } = aurReq;
        const newId = generateUniqueId(); // Generate unique ID for each item

        idsList.push(id);

        return selectedElementsToCopy.push({
          aurReqId: id,
          updatedAurReqs: { [newId]: { aur_parent_key, aur_child_key, value } }
        });
      })
    );

    // Save copied items to Redux state
    const existingCopied = getCopiedItemsFromState();
    const allCopied = [...new Set([...existingCopied, ...idsList])];

    dispatch(clientReqOp.setCopiedElements(allCopied));
    dispatch(clientReqOp.setCheckedElementsToBeCopied(allCopied)); // Keep checkboxes checked
    dispatch(clientReqOp.setNewClientReqs(selectedElementsToCopy));
  };

  const handleFirstConfirm = () => {
    setFirstOpen(false);
    setSecondOpen(true);
  };

  const handleSecondConfirm = () => {
    setSecondOpen(false);
    copyAll();
  };

  const findAurReqById = (aurReqId: number): IAurReq | undefined => {
    for (const key in appliedFiltersResult) {
      const aurReq = appliedFiltersResult[key].AUR_REQ;

      for (const aurChildKey in aurReq) {
        const aurReqObj = aurReq[aurChildKey as keyof typeof aurReq];

        if (aurReqObj.id === aurReqId) {
          return aurReqObj;
        }
      }
    }
    return undefined;
  };

  const mergeSelected = () => {
    const mergedAurReqs: IClientReqToBeAdded[] = [];
    const uniqueAurParentKeys: string[] = [];
    let elementsToBeMerged: number[] = checkedElementsToBeMerged;

    checkedElementsToBeMerged.forEach((aurReqId: number) => {
      const aurReq: IAurReq | undefined = findAurReqById(aurReqId);

      if (aurReq && !uniqueAurParentKeys.includes(aurReq.aur_parent_key)) {
        uniqueAurParentKeys.push(aurReq.aur_parent_key);
      }
    });

    for (const aurParentKey of uniqueAurParentKeys) {
      const aurReqs: IAurReq[] = checkedElementsToBeMerged
        .map((aurReqId) => findAurReqById(aurReqId))
        .filter(
          (aurReq): aurReq is IAurReq =>
            aurReq !== undefined && aurReq.aur_parent_key === aurParentKey
        );

      if (aurReqs.length > 1) {
        const newId = generateUniqueId(); // Generate unique ID for each merge group
        const mergedValues: string = aurReqs
          .map((aurReq) => aurReq.value)
          .join(' ');

        const { aur_child_key, aur_parent_key, id } = aurReqs[0];

        const newClientReq: IClientReq = {
          aur_child_key,
          aur_parent_key,
          value: mergedValues,
          merge_list: aurReqs.map((aurReq: IAurReq) => aurReq.id)
        };

        mergedAurReqs.push({
          aurReqId: id,
          updatedAurReqs: { [newId]: newClientReq }
        });
      } else {
        //TODO remove aurREq id from mergedAurReqs if it has nothing to be merged with
        elementsToBeMerged = checkedElementsToBeMerged.filter(
          (el: number) => el !== aurReqs[0].id
        );
      }
    }

    dispatch(clientReqOp.setNewClientReqs(mergedAurReqs));
    dispatch(clientReqOp.setMergedElements(elementsToBeMerged));
    dispatch(clientReqOp.setCheckedElementsToBeMerged([]));
  };

  const copySelected = () => {
    // Don't filter out already copied items - allow multiple copies
    const itemsToCopy = checkedElementsToBeCopied;

    if (itemsToCopy.length === 0) {
      return;
    }

    const selectedElementsToCopy = itemsToCopy.map((aurReqId: number) => {
      const newId = generateUniqueId();

      const dataObj = appliedFiltersResult.find((item: IRegulatoryReq) =>
        Object.values(item.AUR_REQ).find(
          (aurReqObj: IAurReq) => aurReqObj.id === aurReqId
        )
      );

      // Find the specific AUR requirement that matches the aurReqId
      const specificAurReq =
        dataObj &&
        Object.values(dataObj.AUR_REQ).find(
          (aurReqObj: IAurReq) => aurReqObj.id === aurReqId
        );

      const { aur_parent_key, aur_child_key, value } = specificAurReq || {};

      return {
        aurReqId,
        updatedAurReqs: { [newId]: { aur_parent_key, aur_child_key, value } }
      };
    });

    const existingCopied = getCopiedItemsFromState();
    const allCopied = [...new Set([...existingCopied, ...itemsToCopy])];
    dispatch(clientReqOp.setCopiedElements(allCopied));

    dispatch(clientReqOp.setNewClientReqs(selectedElementsToCopy));

    const currentClientReqs = { ...clientReqs };

    itemsToCopy.forEach((aurReqId: number) => {
      const dataObj = appliedFiltersResult.find((item: IRegulatoryReq) =>
        Object.values(item.AUR_REQ).find(
          (aurReqObj: IAurReq) => aurReqObj.id === aurReqId
        )
      );

      const specificAurReq =
        dataObj &&
        Object.values(dataObj.AUR_REQ).find(
          (aurReqObj: IAurReq) => aurReqObj.id === aurReqId
        );

      if (specificAurReq) {
        if (!currentClientReqs[aurReqId]) {
          currentClientReqs[aurReqId] = {
            client_reqs: {},
            updated_client_reqs: {}
          };
        }

        const existingUpdatedReqs =
          currentClientReqs[aurReqId].updated_client_reqs || {};

        const copySelectedKey = `copy-selected-${generateUniqueId()}`;

        const previewCopy: IClientReq = {
          aur_parent_key: specificAurReq.aur_parent_key,
          aur_child_key: specificAurReq.aur_child_key,
          value: specificAurReq.value,
          bru_id: undefined,
          id: undefined
        };

        currentClientReqs[aurReqId] = {
          ...currentClientReqs[aurReqId],
          updated_client_reqs: {
            ...existingUpdatedReqs,
            [copySelectedKey]: previewCopy
          }
        };
      }
    });

    // Always update the client reqs state to show preview copies
    dispatch(clientReqOp.setClientReqs(currentClientReqs));
  };

  const handleSave = () => {
    if (saveDisabled) {
      return;
    }

    dispatch(complianceEstOp.setSaveClicked(true));
  };

  const handleClientReqSave = () => {
    dispatch(complianceEstOp.setSaveClicked(true));
  };

  useEffect(() => {
    if (lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  const resolveModuleLabels = (moduleIds: any[] = []): string[] => {
    return moduleIds
      .map((id) => {
        const rawId = typeof id === 'object' ? id.value : id;

        return moduleOptionsState.find((o) => o.value === rawId)?.label;
      })
      .filter((label): label is string => Boolean(label));
  };

  const industryArray = Array.isArray(industry)
    ? industry
    : industry
    ? [industry]
    : [];

  const isReadyToApplyFilters =
    !!lang &&
    !!orgContext &&
    Object.keys(orgContext).length > 0 &&
    !!industryArray?.length;

  const applyFilters = async () => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!isReadyToApplyFilters) return;

    const baseParams = Helpers.toApiFilters(orgContext);

    const moduleIds = Array.isArray(orgContext.module)
      ? orgContext.module.map((m: any) => (typeof m === 'object' ? m.value : m))
      : [];

    const resolvedModules = resolveModuleLabels(moduleIds);

    const shouldWaitForModules =
      moduleIds.length > 0 && resolvedModules.length === 0;

    if (shouldWaitForModules) return;

    const filterParams = {
      ...baseParams,
      ...(resolvedModules.length > 0 && {
        module: resolvedModules
      })
    };

    const hasFilters = Object.keys(filterParams).length > 0;

    if (!hasFilters) return;

    localStorage.setItem('orgContext', JSON.stringify(orgContext));

    switch (page) {
      case 'clientReq':
        complianceEstOp.applyFiltersApi(lang, filterParams).then((data) => {
          dispatch(clientReqOp.setAppliedFilersResult(data || []));
        });

        dispatch(complianceEstOp.setCommentsData([]));
        break;

      case 'gapsAndAuditFindings':
        if (activeTab) {
          const gapsReqs = gapsRequests(dispatch, lang, filterParams);

          switch (activeTab) {
            case GapTabs.AUDIT_FINDINGS:
              dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
              break;

            case GapTabs.EVIDENCES:
              dispatch(gapsOp.setAssignedEvidencesUpdated(false));
              break;

            case GapTabs.GAPS_DESC:
              dispatch(gapsOp.setGapsUpdated(false));
              break;

            case GapTabs.ADDITIONAL_INFO:
              dispatch(gapsOp.setInfoUpdated(false));
              break;

            default:
              return;
          }

          gapsReqs[activeTab as GapTabs]();
        }
        break;

      case 'solutionDefinition':
        solutionsOp.fetchSolutions(lang, filterParams).then((data) => {
          dispatch(solutionsOp.setSolutions(data?.flat()));
        });
        break;

      case 'tasksDefinition':
        tasksOp.fetchTasks(lang, filterParams).then((data) => {
          dispatch(tasksOp.setTasks(data?.flat()));
        });
        break;

      case 'evidenceDefinition':
        evidencesOp.fetchEvidences(lang, filterParams).then((data) => {
          dispatch(evidencesOp.setEvidences(data?.flat()));
        });
        break;

      default:
        return;
    }

    dispatch(complianceEstOp.setDrawTable(true));
  };

  const moduleKey = useMemo(
    () => JSON.stringify(orgContext?.module || []),
    [orgContext?.module]
  );

  const moduleOptionsLength = moduleOptionsState?.length || 0;

  useEffect(() => {
    if (!isReadyToApplyFilters) return;

    applyFilters();
  }, [activeTab, moduleKey, moduleOptionsLength]);

  useEffect(() => {
    if (tasksUpdated) {
      applyFilters();
      dispatch(complianceEstOp.setTasksUpdated(false));
    }
  }, [tasksUpdated]);

  /* ---------------- fetch modules ---------------- */

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const moduleGroups = Array.isArray(orgContext.moduleGroup)
          ? orgContext.moduleGroup
          : orgContext.moduleGroup
          ? [orgContext.moduleGroup]
          : [];

        if (!moduleGroups.length) {
          setModuleOptionsState([]);
          return;
        }

        const ids = moduleGroups.map((item: any) =>
          typeof item === 'object' ? item.value : item
        );

        const res = await ApiService.getModulesByModuleGroups({
          module_group_id: ids
        });

        const modules = res?.data?.data ?? [];

        const unique = Array.from(
          new Map(
            modules
              .filter((m: any) => m?.mod_id != null)
              .map((m: any) => [m.mod_id, m])
          ).values()
        );

        setModuleOptionsState(
          unique.map((m: any) => ({
            value: m.mod_id,
            label: m.mod_short_name
          }))
        );
      } catch (err) {
        console.error('Failed to fetch modules:', err);
        setModuleOptionsState([]);
      }
    };

    fetchModules();
  }, [JSON.stringify(orgContext.moduleGroup)]);

  const selectedModuleValues = Array.isArray(orgContext.module)
    ? orgContext.module.map((m: any) => m.value ?? m)
    : [];

  useEffect(() => {
    dispatch(complianceEstOp.setFilters(lang));
  }, []);

  useEffect(() => {
    dispatch(complianceEstOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  useEffect(() => {
    if (!isReadyToApplyFilters) return;
    if (gapsUpdated || infoUpdated) {
      applyFilters();
      dispatch(gapsOp.setGapsUpdated(false));
      dispatch(gapsOp.setInfoUpdated(false));
    }
  }, [gapsUpdated, infoUpdated]);

  const titleKeyCompliance: TitleKeyComplianceScreens = (() => {
    switch (page) {
      case 'clientReq':
        return 'DEFINE_CLIENT_REQ';
      case 'gapsAndAuditFindings':
        return 'ADD_GAPS_AND_AUDIT_FINDINGS';
      case 'solutionDefinition':
        return 'SOLUTION_DEFINITIONS';
      case 'tasksDefinition':
        return 'TASKS_DEFINITIONS';
      case 'evidenceDefinition':
        return 'EVIDENCE_DEFINITIONS';
      default:
        return 'DEFINE_CLIENT_REQ';
    }
  })();

  return (
    <Styled.StyledLayout token={token}>
      <Styled.GlobalThemeStyles scheme={scheme} />
      <TitleBar
        title={t(titleKeyCompliance)}
        goBackButtons={
          <>
            <GoBackButton
              to='/workingAreaDetails/rules-to-evidence'
              text={t('GO_BACK')}
              checkUnsavedChanges={true}
            />
            <GoBackButton
              to='/working_area'
              text={t('GO_TO_WORKING_AREAS')}
              checkUnsavedChanges={true}
            />
          </>
        }
      />
      <Styled.StepsContainer scheme={scheme} lang={lang}>
        <Steps
          current={current === -1 ? 0 : current}
          items={items}
          onChange={handleStepChange}
        />
      </Styled.StepsContainer>
      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        <Styled.FiltersContainer>
          <Styled.FiltersSubtitle>
            {t('SELECTED_FOCUS_AREAS', {
              defaultValue: 'Selected Focus Areas'
            })}
          </Styled.FiltersSubtitle>
          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('MODULE')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedModuleValues)
                ? selectedModuleValues
                : [selectedModuleValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    moduleOptionsState.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>

          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('GROUP')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedGroupValues)
                ? selectedGroupValues
                : [selectedGroupValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    groupOptions.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>
        </Styled.FiltersContainer>
        <Styled.ActionButtons
          isClientReqPage={page === 'clientReq'}
          hasDisplayedData={drawTable && page === 'clientReq'}
          isCompliancePage={page !== 'clientReq' && !drawTable}
          lang={lang}
        >
          <Styled.ApplyButton
            type='primary'
            onClick={applyFilters}
            scheme={scheme}
          >
            {t('DISPLAY')}
          </Styled.ApplyButton>
          {page === 'clientReq' && drawTable && (
            <>
              <Styled.ApplyButton
                onClick={mergeSelected}
                disabled={checkedElementsToBeMerged.length <= 1}
                scheme={scheme}
              >
                {t('MERGE_SELECTED')}
              </Styled.ApplyButton>
              <Styled.ApplyButton
                onClick={copySelected}
                disabled={checkedElementsToBeCopied.length === 0}
                scheme={scheme}
              >
                {t('COPY_SELECTED')} ({checkedElementsToBeCopied.length})
              </Styled.ApplyButton>
              <>
                <Popconfirm
                  title={t('COPY_ALL_CONFIRM')}
                  open={firstOpen}
                  onConfirm={handleFirstConfirm}
                  onCancel={() => setFirstOpen(false)}
                  okText={t('YES')}
                  cancelText={t('NO')}
                >
                  <Styled.ApplyButton
                    disabled={
                      !!(copiedElements.length || mergedElements.length)
                    }
                    scheme={scheme}
                    onClick={() => setFirstOpen(true)}
                  >
                    {t('COPY_ALL')}
                  </Styled.ApplyButton>
                </Popconfirm>

                <Popconfirm
                  title={t('COPY_ALL_CONFIRM_2')}
                  open={secondOpen}
                  onConfirm={handleSecondConfirm}
                  onCancel={() => setSecondOpen(false)}
                  okText={t('COPY_ALL_CONFIRM_OK')}
                  cancelText={t('NO')}
                >
                  <span />
                </Popconfirm>
              </>
            </>
          )}
          {(drawTable || !saveDisabled) && (
            <Styled.ApplyButton
              disabled={saveDisabled}
              onClick={page === 'clientReq' ? handleClientReqSave : handleSave}
              scheme={scheme}
            >
              {t('SAVE')}
            </Styled.ApplyButton>
          )}
        </Styled.ActionButtons>
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider />
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default ComplianceEstLayout;
